import { useEffect, useState } from 'react';
import DomainBoard from '../components/DomainBoard.js';
import FindingsList, { type AuditFinding } from '../components/FindingsList.js';
import RunAuditControl from '../components/RunAuditControl.js';

const CTO_DOMAIN_LABELS: Record<string, string> = {
  system_design: 'System Design',
  system_architecture: 'System Architecture',
  frontend: 'Frontend',
  apis_backend: 'APIs & Backend',
  databases_storage: 'Databases & Storage',
  auth_permissions: 'Auth & Permissions',
  hosting_cloud: 'Hosting & Cloud',
  cicd_version_control: 'CI/CD & Version Control',
  rate_limiting: 'Rate Limiting',
  caching_cdn: 'Caching & CDN',
  error_tracking_logs: 'Error Tracking & Logs',
  monitoring_alerts: 'Monitoring & Alerts',
  testing: 'Testing',
  scaling_performance: 'Scaling & Performance',
  cost_efficiency: 'Cost Efficiency',
  data_lifecycle_backups: 'Data Lifecycle & Backups',
  dependency_health: 'Dependency Health',
  documentation_dx: 'Documentation & DX',
};

const REPOS = ['sidwellgrav/conductor', 'sidwellgrav/biltyn'];
const SEVERITIES = ['info', 'warn', 'high', 'critical'] as const;

interface DomainVerdict {
  domain: string;
  status: string;
  note: string;
}

interface Audit {
  id: string;
  auditor: 'cto' | 'ciso';
  repo: string;
  trigger: 'weekly' | 'on_demand';
  state: string;
  overall_status: 'green' | 'flag' | 'critical' | null;
  summary: string | null;
  domains: DomainVerdict[];
  session_url: string | null;
  error: string | null;
  started_at: string;
  completed_at: string | null;
}

interface PostureEntry {
  auditor: 'cto' | 'ciso';
  repo: string;
  latest_audit: Audit | null;
  open_findings_by_severity: Record<string, number>;
  domains: DomainVerdict[];
}

function repoShortName(repo: string): string {
  return repo.split('/')[1] ?? repo;
}

function postureBadgeClass(status: string): string {
  if (status === 'green') return 'badge-green';
  if (status === 'flag') return 'badge-yellow';
  if (status === 'critical') return 'badge-red';
  return 'badge-gray';
}

function statusRank(status: string | null): number {
  if (status === 'critical') return 2;
  if (status === 'flag') return 1;
  if (status === 'green') return 0;
  return -1;
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
  return body as T;
}

async function postJson(path: string): Promise<void> {
  const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
}

function AutomationToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>('/api/settings/audit-automation')
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/audit-automation', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        Automatic audits
      </label>
      {enabled === false && <span className="card-sub">on-demand only</span>}
    </div>
  );
}

function AutopilotToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>('/api/settings/autopilot')
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/autopilot', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        Autopilot (Architect auto-approve)
      </label>
      {enabled === false && <span className="card-sub">drafted ideas await human review</span>}
    </div>
  );
}

function LlmBudgetControl() {
  const [budget, setBudget] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ budget_usd: number | null }>('/api/settings/llm-budget')
      .then((r) => setBudget(r.budget_usd))
      .catch(() => setBudget(null));
  }, []);

  async function update(next: number) {
    if (busy) return;
    const prev = budget;
    setBudget(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/llm-budget', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget_usd: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setBudget(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Daily LLM budget ($)
        <input
          type="number"
          min={0}
          max={500}
          step={1}
          value={budget ?? ''}
          disabled={busy}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) update(next);
          }}
          style={{ width: 80 }}
        />
      </label>
      <span className="card-sub">0 disables reviews</span>
    </div>
  );
}

export default function Engineering() {
  const [posture, setPosture] = useState<PostureEntry[]>([]);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [history, setHistory] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [repoFilter, setRepoFilter] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetchJson<{ posture: PostureEntry[] }>('/api/audit-posture'),
      fetchJson<{ findings: AuditFinding[] }>('/api/audit-findings?auditor=cto&status=open'),
      fetchJson<{ audits: Audit[] }>('/api/audits?auditor=cto'),
    ])
      .then(([p, f, a]) => {
        setPosture(p.posture.filter((entry) => entry.auditor === 'cto'));
        setFindings(f.findings);
        setHistory(a.audits.slice(0, 10));
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  function selectDomain(repo: string, domain: string) {
    if (repoFilter === repo && domainFilter === domain) {
      setRepoFilter(null);
      setDomainFilter(null);
    } else {
      setRepoFilter(repo);
      setDomainFilter(domain || null);
    }
  }

  async function ack(id: string) {
    const prev = findings;
    setBusyId(id);
    setFindings((fs) => fs.map((f) => (f.id === id ? { ...f, status: 'acknowledged' } : f)));
    try {
      await postJson(`/api/audit-findings/${id}/ack`);
    } catch (e) {
      setFindings(prev);
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function resolve(id: string) {
    const prev = findings;
    setBusyId(id);
    setFindings((fs) => fs.map((f) => (f.id === id ? { ...f, status: 'resolved' } : f)));
    try {
      await postJson(`/api/audit-findings/${id}/resolve`);
    } catch (e) {
      setFindings(prev);
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="empty">Loading…</div>;

  const worst = posture.reduce<PostureEntry | null>((acc, entry) => {
    if (!entry.latest_audit) return acc;
    if (!acc || statusRank(entry.latest_audit.overall_status) > statusRank(acc.latest_audit?.overall_status ?? null)) {
      return entry;
    }
    return acc;
  }, null);

  const visibleFindings = findings.filter((f) => {
    if (repoFilter && f.repo !== repoFilter) return false;
    if (domainFilter && f.domain !== domainFilter) return false;
    if (severityFilter && f.severity !== severityFilter) return false;
    return true;
  });

  return (
    <div>
      {error && <div className="errors">{error}</div>}

      <div className="section">
        <AutomationToggle />
        <AutopilotToggle />
        <LlmBudgetControl />
      </div>

      <div className="section">
        <h3>Engineering posture</h3>
        {worst?.latest_audit ? (
          <>
            <div className="badge-row">
              <span className={`badge ${postureBadgeClass(worst.latest_audit.overall_status ?? 'gray')}`}>
                {worst.latest_audit.overall_status}
              </span>
              <span className="card-sub">
                {repoShortName(worst.repo)} — audited {relativeTime(worst.latest_audit.completed_at ?? worst.latest_audit.started_at)}
              </span>
            </div>
            <div className="one-line">{worst.latest_audit.summary}</div>
          </>
        ) : (
          <div className="empty">No CTO audit has run yet.</div>
        )}
      </div>

      <RunAuditControl auditor="cto" />

      {posture.map((entry) => (
        <DomainBoard
          key={entry.repo}
          title={`${repoShortName(entry.repo)} — domains`}
          domains={entry.domains}
          labels={CTO_DOMAIN_LABELS}
          selected={repoFilter === entry.repo ? domainFilter ?? undefined : undefined}
          onSelect={(d) => selectDomain(entry.repo, d)}
        />
      ))}

      <div className="section">
        <h3>Open findings</h3>
        <div className="badge-row" style={{ flexWrap: 'wrap', marginBottom: 10 }}>
          {SEVERITIES.map((s) => (
            <button
              key={s}
              className={`badge ${severityFilter === s ? 'badge-red' : 'badge-gray'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => setSeverityFilter(severityFilter === s ? null : s)}
            >
              {s}
            </button>
          ))}
          {REPOS.map((r) => (
            <button
              key={r}
              className={`badge ${repoFilter === r ? 'badge-green' : 'badge-gray'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => setRepoFilter(repoFilter === r ? null : r)}
            >
              {repoShortName(r)}
            </button>
          ))}
          {domainFilter && (
            <button
              className="badge badge-yellow"
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => setDomainFilter(null)}
            >
              {CTO_DOMAIN_LABELS[domainFilter] ?? domainFilter} ✕
            </button>
          )}
        </div>
        <FindingsList
          findings={visibleFindings}
          labels={CTO_DOMAIN_LABELS}
          onAck={ack}
          onResolve={resolve}
          busyId={busyId}
        />
      </div>

      <div className="section">
        <h3>Audit history</h3>
        {history.length === 0 && <div className="empty">No audits recorded yet.</div>}
        {history.map((a) => (
          <div key={a.id} className="card" style={{ display: 'block' }}>
            <div className="badge-row" style={{ flexWrap: 'wrap' }}>
              <span className={`badge ${postureBadgeClass(a.overall_status ?? 'gray')}`}>
                {a.overall_status ?? a.state}
              </span>
              <span className="card-sub">{repoShortName(a.repo)}</span>
              <span className="card-sub">{a.trigger}</span>
              <span className="card-sub">{new Date(a.started_at).toLocaleString()}</span>
              {a.session_url && (
                <a className="card-sub" href={a.session_url} target="_blank" rel="noreferrer">session</a>
              )}
            </div>
            {a.summary && <div className="one-line">{a.summary}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
